import { useCallback, useEffect, useRef, useState } from "react";

import { useSelector } from "react-redux";
import { useMatch } from "react-router-dom";

import { selectSnapshotIsPowerDialTaskWaiting } from "Services/state/conferences/ConferenceSnapshotSelectors";
import { useRVDispatch } from "Services/state/Storage";
import { acceptTaskThunk, setActiveTask } from "Services/state/tasks/Thunks";

import { logger } from "../../CallBar.utils";

import type { RVTask } from "@regal-voice/shared-types";

export type UseCallActionAcceptTaskOptions = {
    activeConferenceTask?: RVTask;
    deviceCallSid?: string;
};

export type UseCallActionAcceptTaskResult = {
    acceptTask: () => void;
    acceptActiveTask: () => void;
    acceptingCall: boolean;
};

export const useCallActionAcceptTask = ({
    activeConferenceTask,
    deviceCallSid,
}: UseCallActionAcceptTaskOptions = {}): UseCallActionAcceptTaskResult => {
    const dispatch = useRVDispatch();
    const [acceptingCall, setAcceptingCall] = useState(false);
    const alreadyAcceptedTaskSids = useRef<string[]>([]);

    const isPowerDialTaskWaiting = useSelector(selectSnapshotIsPowerDialTaskWaiting);
    const isAgentDesktopPage = useMatch("/agent/:sub?/:childSub?");

    const acceptTask = useCallback(() => {
        if (!activeConferenceTask || alreadyAcceptedTaskSids.current?.includes(activeConferenceTask.sid)) {
            return;
        }

        setAcceptingCall(true);
        alreadyAcceptedTaskSids.current.push(activeConferenceTask.sid);
        dispatch(acceptTaskThunk(activeConferenceTask, "manual"));
    }, [activeConferenceTask, dispatch, setAcceptingCall, alreadyAcceptedTaskSids]);

    const acceptActiveTask = useCallback(() => {
        dispatch(setActiveTask(activeConferenceTask));
        acceptTask();
    }, [dispatch, activeConferenceTask, acceptTask]);

    useEffect(() => {
        const shouldAcceptPowerDialTask = isPowerDialTaskWaiting && !isAgentDesktopPage;

        if (shouldAcceptPowerDialTask) {
            acceptTask();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to trigger when there's a task waiting
    }, [isPowerDialTaskWaiting]);

    useEffect(() => {
        if (deviceCallSid && activeConferenceTask?.status === "accepted") {
            logger.log("Call controls switched from inactive to active", {
                connectionCallSid: deviceCallSid,
            });

            setAcceptingCall(false);
        }
        // We only care about the `deviceCallSid` changing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceCallSid]);

    return {
        acceptTask,
        acceptActiveTask,
        acceptingCall,
    };
};
